import React, {createContext, useContext, useMemo} from 'react'
import {useOptimizelyPage} from '../hooks/use-optimizely-page'
import {extractAllGlobalElements} from '../utils/global-elements-utils'
import {useGlobalPreviewContent, mergePreviewIntoGlobal} from './global-preview-context'

const GlobalElementsContext = createContext()

// Debug flag - set to true to enable detailed logging
const DEBUG_GLOBAL_ELEMENTS = true

export const GlobalElementsProvider = ({children}) => {
    // Fetch global elements content from the 'grg-web-global-components' page
    const {data: globalContent, isLoading: isGlobalLoading, error: globalError} = useOptimizelyPage('grg-web-global-components')

    // Check if we have preview content for global elements
    const previewContent = useGlobalPreviewContent()

    // Extract and organize global data
    const globalData = useMemo(() => {
        if (DEBUG_GLOBAL_ELEMENTS) {
            console.log('🌍 GlobalElementsContext - Raw data from grg-web-global-components:', globalContent)
            if (previewContent) {
                console.log('🔄 GlobalElementsContext - Preview content detected:', previewContent)
            }
        }

        if (!globalContent?.items?.length) {
            if (DEBUG_GLOBAL_ELEMENTS) {
                console.log('⚠️ GlobalElementsContext - No items found in global content')
            }
            return {
                header: null,
                footer: null,
                search: null,
                navigation: null,
                topBanner: null,
                isFromCMS: false,
                isPreviewMode: !!previewContent
            }
        }

        // Merge preview content into global content if available
        const contentToExtract = previewContent
            ? mergePreviewIntoGlobal(globalContent, previewContent)
            : globalContent

        if (DEBUG_GLOBAL_ELEMENTS && previewContent) {
            console.log('🔀 GlobalElementsContext - Merged content:', contentToExtract)
        }

        // Extract all global elements using the utility functions
        const extractedElements = extractAllGlobalElements(contentToExtract)
        if (DEBUG_GLOBAL_ELEMENTS) {
            console.log('🔍 GlobalElementsContext - Extracted elements:', extractedElements)
        }

        const finalData = {
            header: extractedElements.header,
            footer: extractedElements.footer,
            search: extractedElements.search,
            badges: extractedElements.badges,
            navigation: extractedElements.navigation,
            topBanner: extractedElements.topBanner,
            isFromCMS: true,
            isLoading: isGlobalLoading,
            error: globalError,
            rawData: contentToExtract,
            isPreviewMode: !!previewContent
        }

        if (DEBUG_GLOBAL_ELEMENTS) {
            console.log('✅ GlobalElementsContext - Final processed data:', finalData)
        }
        return finalData
    }, [globalContent, previewContent, isGlobalLoading, globalError])
    
    return (
        <GlobalElementsContext.Provider value={globalData}>
            {children}
        </GlobalElementsContext.Provider>
    )
}

export const useGlobalElements = () => {
    const context = useContext(GlobalElementsContext)
    if (!context) {
        throw new Error('useGlobalElements must be used within GlobalElementsProvider')
    }
    return context
}

// Convenience hooks for specific global elements
export const useGlobalHeader = () => {
    const {header, isLoading, error} = useGlobalElements()
    return {data: header, isLoading, error}
}

export const useGlobalFooter = () => {
    const {footer, isLoading, error} = useGlobalElements()
    return {data: footer, isLoading, error}
}

export const useGlobalListOfLinks = () => {
    const {footer, isLoading, error} = useGlobalElements()
    return {data: footer?.listOfLinks, isLoading, error}
}

export const useGlobalNewsletterSubscription = () => {
    const {footer, isLoading, error} = useGlobalElements()
    return {data: footer?.newsletterSubscription, isLoading, error}
}

export const useGlobalGoToApp = () => {
    const {footer, isLoading, error} = useGlobalElements()
    return {data: footer?.goToApp, isLoading, error}
}

export const useGlobalBottomBar = () => {
    const {footer, isLoading, error} = useGlobalElements()
    return {data: footer?.bottomBar, isLoading, error}
}

export const useGlobalSearch = () => {
    const {search, isLoading, error} = useGlobalElements()
    return {data: search, isLoading, error}
}

export const useGlobalNavigation = () => {
    const {navigation, isLoading, error} = useGlobalElements()
    return {data: navigation, isLoading, error}
}

export const useGlobalBadges = () => {
    const {badges, isLoading, error} = useGlobalElements()
    return {data: badges, isLoading, error}
}

export const useGlobalTopBanner = () => {
    const {topBanner, isLoading, error} = useGlobalElements()
    return {data: topBanner, isLoading, error}
}